import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

type UnboxingGateProps = {
  /** Couple or event title shown on the sealed face */
  title: string;
  /** Accent colour for seal / foil (CSS colour) */
  accent?: string;
  /** Background behind the sealed card */
  background?: string;
  children: React.ReactNode;
};

/**
 * Optional theatrical entry: guest taps a wax seal to open the invitation.
 * Lightweight CSS/Motion — safe on low-end Android (no Three.js).
 */
export function UnboxingGate({
  title,
  accent = '#D4AF37',
  background = '#0a0f0c',
  children,
}: UnboxingGateProps) {
  const [opened, setOpened] = useState(false);

  // absolute (not fixed): must stay inside TemplateCardPreview / modal frames.
  // fixed inset-0 leaked over the whole wizard and blocked template selection.
  return (
    <div className="relative min-h-[100dvh] w-full" data-testid="unboxing-root">
    <AnimatePresence mode="wait">
      {!opened ? (
        <motion.div
          key="gate"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 0.45 }}
          className="absolute inset-0 z-[100] flex flex-col items-center justify-center px-6"
          style={{
            background: `radial-gradient(ellipse at 50% 40%, ${accent}22 0%, transparent 45%), ${background}`,
          }}
          data-testid="unboxing-gate"
          onClick={(e) => e.stopPropagation()}
        >
          <p
            className="mb-8 text-center text-[11px] font-medium uppercase tracking-[0.38em]"
            style={{ color: accent, fontFamily: 'Fraunces, Georgia, serif' }}
          >
            You are invited
          </p>
          <motion.button
            type="button"
            aria-label="Break the wax seal to open the invitation"
            onClick={(e) => {
              e.stopPropagation();
              setOpened(true);
            }}
            className="relative flex h-56 w-56 flex-col items-center justify-center rounded-full border-2 bg-black/50 shadow-2xl backdrop-blur-md"
            style={{
              borderColor: accent,
              boxShadow: `0 0 56px ${accent}44, inset 0 0 40px ${accent}18`,
            }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
          >
            <span
              className="mb-2 text-5xl font-bold"
              style={{ color: accent, fontFamily: 'Fraunces, Georgia, serif' }}
              aria-hidden
            >
              ✦
            </span>
            <span
              className="max-w-[10rem] text-center text-sm leading-snug"
              style={{ color: accent, fontFamily: 'Fraunces, Georgia, serif' }}
            >
              {title || 'Open Invitation'}
            </span>
            <span className="mt-3 text-[10px] uppercase tracking-[0.22em] text-white/55">
              Tap seal to open
            </span>
          </motion.button>
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
    </div>
  );
}
